/* import __COLOCATED_TEMPLATE__ from './quarterfinal-startlist-interface.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

export default class HeatStartlistComponent extends Component
{
	@service store;
	@service csv;
	@service sprintTools;
	
	@tracked sourceEvent;
	@tracked sourceClassGroup;

	@tracked bibOffset = 0;
	@tracked resultsOffset = 0;

	// Class filters - when checked, only athletes with matching className are included
	@tracked filterU14 = false;
	@tracked filterU16 = false;
	@tracked filterU18 = false;
	@tracked filterU20 = false;

	get hasClassFilter() {
		return this.filterU14 || this.filterU16 || this.filterU18 || this.filterU20;
	}

	get selectedClassFilters() {
		const filters = [];
		if (this.filterU14) filters.push('U14');
		if (this.filterU16) filters.push('U16');
		if (this.filterU18) filters.push('U18');
		if (this.filterU20) filters.push('U20');
		return filters;
	}

	get classFilterDisplay() {
		if (!this.hasClassFilter) return '';
		return this.selectedClassFilters.join(', ');
	}

	get resultsRangeStart() {
		return (parseInt(this.resultsOffset) || 0) + 1;
	}

	get resultsRangeEnd() {
		return (parseInt(this.resultsOffset) || 0) + 30;
	}
	
	@tracked Q1ClassGroup;
	@tracked Q2ClassGroup;
	@tracked Q3ClassGroup;
	@tracked Q4ClassGroup;
	@tracked Q5ClassGroup;
	
	@tracked top30Results;
	@tracked Q1Assignments;
	@tracked Q2Assignments;
	@tracked Q3Assignments;
	@tracked Q4Assignments;
	@tracked Q5Assignments;
	
	@tracked startTimeQ1;
	@tracked startTimeQ2;
	@tracked startTimeQ3;
	@tracked startTimeQ4;
	@tracked startTimeQ5;
	
	@action
	async sourceEventChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.sourceEvent = null;
			this.sourceClassGroup = null;
		}
		else
		{
			var event = await this.store.findRecord('Event', browserEvent.target.value);
			this.sourceEvent = event;
			this.sourceClassGroup = null;
		}
		
		this.updateResults();
	}
	
	@action
	async classGroupChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.sourceClassGroup = null;
			this.updateResults();
		}
		else
		{
			var classGroup = await this.store.findRecord('ClassGroup', browserEvent.target.value);
			this.sourceClassGroup = classGroup;
		}
		
		this.updateResults();
	}
	
	@action
	bibOffsetChanged(browserEvent)
	{
		browserEvent.preventDefault();
		this.updateHeatAssignments();
	}

	@action
	resultsOffsetChanged(browserEvent)
	{
		browserEvent.preventDefault();
		this.updateResults();
	}

	@action
	toggleClassFilter(filterName)
	{
		if (filterName === 'U14') this.filterU14 = !this.filterU14;
		else if (filterName === 'U16') this.filterU16 = !this.filterU16;
		else if (filterName === 'U18') this.filterU18 = !this.filterU18;
		else if (filterName === 'U20') this.filterU20 = !this.filterU20;
		this.updateResults();
	}
	
	@action
	tieSelectionChanged(result, browserEvent)
	{
		result.set('useResultForTie', browserEvent.target.checked);
		this.updateHeatAssignments();
	}
	
	@action
	async Q1ClassGroupChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.Q1ClassGroup = null;
		}
		else
		{
			var classGroup = await this.store.findRecord('ClassGroup', browserEvent.target.value);
			this.Q1ClassGroup = classGroup;
			this.startTimeQ1 = dayjs(classGroup.startTime).format("HH:mm:ss");
			this.updateHeatAssignments();
		}
	}
	
	@action
	async Q2ClassGroupChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.Q2ClassGroup = null;
		}
		else
		{
			var classGroup = await this.store.findRecord('ClassGroup', browserEvent.target.value);
			this.Q2ClassGroup = classGroup;
			this.startTimeQ2 = dayjs(classGroup.startTime).format("HH:mm:ss");
			this.updateHeatAssignments();
		}
	}
	
	@action
	async Q3ClassGroupChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.Q3ClassGroup = null;
		}
		else
		{
			var classGroup = await this.store.findRecord('ClassGroup', browserEvent.target.value);
			this.Q3ClassGroup = classGroup;
			this.startTimeQ3 = dayjs(classGroup.startTime).format("HH:mm:ss");
			this.updateHeatAssignments();
		}
	}
	
	@action
	async Q4ClassGroupChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.Q4ClassGroup = null;
		}
		else
		{
			var classGroup = await this.store.findRecord('ClassGroup', browserEvent.target.value);
			this.Q4ClassGroup = classGroup;
			this.startTimeQ4 = dayjs(classGroup.startTime).format("HH:mm:ss");
			this.updateHeatAssignments();
		}
	}
	
	@action
	async Q5ClassGroupChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.Q5ClassGroup = null;
		}
		else
		{
			var classGroup = await this.store.findRecord('ClassGroup', browserEvent.target.value);
			this.Q5ClassGroup = classGroup;
			this.startTimeQ5 = dayjs(classGroup.startTime).format("HH:mm:ss");
			this.updateHeatAssignments();
		}
	}
	
	@action
	exportHeatStartListCSV()
	{
		// Check if assignments exist before exporting
		if (!this.Q1Assignments || !this.Q2Assignments || !this.Q3Assignments || !this.Q4Assignments || !this.Q5Assignments) {
			return;
		}

		var buildData = (assignments, classGroup, startTime) => {
			var data = [...this.sprintTools.getCSVHeaders()];
			for (const result of assignments) {
				data.push(this.sprintTools.buildCSVRow(result, classGroup, startTime, result.newBib));
			}
			return data;
		};

		this.csv.export(buildData(this.Q1Assignments, this.Q1ClassGroup, this.startTimeQ1), {fileName:'Q1quarterfinalsStartlist.csv',withSeparator:false});
		this.csv.export(buildData(this.Q2Assignments, this.Q2ClassGroup, this.startTimeQ2), {fileName:'Q2quarterfinalsStartlist.csv',withSeparator:false});
		this.csv.export(buildData(this.Q3Assignments, this.Q3ClassGroup, this.startTimeQ3), {fileName:'Q3quarterfinalsStartlist.csv',withSeparator:false});
		this.csv.export(buildData(this.Q4Assignments, this.Q4ClassGroup, this.startTimeQ4), {fileName:'Q4quarterfinalsStartlist.csv',withSeparator:false});
		this.csv.export(buildData(this.Q5Assignments, this.Q5ClassGroup, this.startTimeQ5), {fileName:'Q5quarterfinalsStartlist.csv',withSeparator:false});
	}
	
	@action
	async createClasses()
	{
		var destinationEvent = this.args.destinationEvent;
		var destinationClasses = await destinationEvent.get('classes');

		await this.sprintTools.createClassesForHeat(destinationEvent, destinationClasses, this.Q1ClassGroup, this.Q1Assignments);
		await this.sprintTools.createClassesForHeat(destinationEvent, destinationClasses, this.Q2ClassGroup, this.Q2Assignments);
		await this.sprintTools.createClassesForHeat(destinationEvent, destinationClasses, this.Q3ClassGroup, this.Q3Assignments);
		await this.sprintTools.createClassesForHeat(destinationEvent, destinationClasses, this.Q4ClassGroup, this.Q4Assignments);
		await this.sprintTools.createClassesForHeat(destinationEvent, destinationClasses, this.Q5ClassGroup, this.Q5Assignments);
	}
	
	@action
	async createEntries()
	{
		var destinationEvent = this.args.destinationEvent;
		var destinationEntries = await destinationEvent.get('entries');
		var destinationClasses = await destinationEvent.get('classes');

		//this is done out of order to make it easy to swap 21 and 22 from Q1 and Q4 to Q2 and Q3 and still keep the ids
		//in order for each heat so the heat start lists print correctly
		//totally a hack for a specific case of less than 30 starters
		await this.sprintTools.createEntriesForHeat(destinationEvent, destinationEntries, destinationClasses, this.Q2ClassGroup, this.Q2Assignments, this.startTimeQ2);
		await this.sprintTools.createEntriesForHeat(destinationEvent, destinationEntries, destinationClasses, this.Q3ClassGroup, this.Q3Assignments, this.startTimeQ3);
		await this.sprintTools.createEntriesForHeat(destinationEvent, destinationEntries, destinationClasses, this.Q1ClassGroup, this.Q1Assignments, this.startTimeQ1);
		await this.sprintTools.createEntriesForHeat(destinationEvent, destinationEntries, destinationClasses, this.Q4ClassGroup, this.Q4Assignments, this.startTimeQ4);
		await this.sprintTools.createEntriesForHeat(destinationEvent, destinationEntries, destinationClasses, this.Q5ClassGroup, this.Q5Assignments, this.startTimeQ5);
	}
	
	updateHeatAssignments()
	{		
		var bibOffset = parseInt(this.bibOffset);
		
		//only assign heats if all class groups have valid selections
		if(	this.Q1ClassGroup == null || 
			this.Q2ClassGroup == null || 
			this.Q3ClassGroup == null ||
			this.Q4ClassGroup == null ||
			this.Q5ClassGroup == null)
		{
			return;
		}
		
		//only assign heats if the top30Results is populated fully
		if(this.top30Results == null)// || this.top30Results.length != 30)
		{
			return;
		}
		
		//per table 325.4.2.5 in FIS ICR
		var Q1Assignments = [];
		var Q2Assignments = [];
		var Q3Assignments = [];
		var Q4Assignments = [];
		var Q5Assignments = [];
		
		//Q1: 1,10,11,20,21,30
		if(this.top30Results.length > 0)
		{
			this.top30Results[0].set('newBib',1+bibOffset);
			this.top30Results[0].set('heatStartOrder',1);
			Q1Assignments.push(this.top30Results[0]);
		}
		if(this.top30Results.length > 9)
		{
			this.top30Results[9].set('newBib',10+bibOffset);
			this.top30Results[9].set('heatStartOrder',2);
			Q1Assignments.push(this.top30Results[9]);
		}
		if(this.top30Results.length > 10)
		{
			this.top30Results[10].set('newBib',11+bibOffset);
			this.top30Results[10].set('heatStartOrder',3);
			Q1Assignments.push(this.top30Results[10]);
		}
		if(this.top30Results.length > 19)
		{
			this.top30Results[19].set('newBib',20+bibOffset);
			this.top30Results[19].set('heatStartOrder',4);
			Q1Assignments.push(this.top30Results[19]);
		}
		if(this.top30Results.length > 20)
		{
			this.top30Results[20].set('newBib',21+bibOffset);
			this.top30Results[20].set('heatStartOrder',5);
			Q1Assignments.push(this.top30Results[20]);
		}
		if(this.top30Results.length > 29)
		{
			if(this.top30Results[29].isTie && this.top30Results.length > 30)
			{
				if(this.top30Results[29].useResultForTie)
				{
					this.top30Results[29].set('newBib',30+bibOffset);
					this.top30Results[29].set('heatStartOrder',6);
					Q1Assignments.push(this.top30Results[29]);
				}
				if(this.top30Results[30].useResultForTie)
				{
					this.top30Results[30].set('newBib',30+bibOffset);
					this.top30Results[30].set('heatStartOrder',6);
					Q1Assignments.push(this.top30Results[30]);
				}
			}
			else
			{
				this.top30Results[29].set('newBib',30+bibOffset);
				this.top30Results[29].set('heatStartOrder',6);
				Q1Assignments.push(this.top30Results[29]);
			}
		}
		
		//Q2: 4,7,14,17,24,27
		if(this.top30Results.length > 3)
		{
			this.top30Results[3].set('newBib',4+bibOffset);
			this.top30Results[3].set('heatStartOrder',1);
			Q2Assignments.push(this.top30Results[3]);
		}
		if(this.top30Results.length > 6)
		{
			this.top30Results[6].set('newBib',7+bibOffset);
			this.top30Results[6].set('heatStartOrder',2);
			Q2Assignments.push(this.top30Results[6]);
		}
		if(this.top30Results.length > 13)
		{
			this.top30Results[13].set('newBib',14+bibOffset);
			this.top30Results[13].set('heatStartOrder',3);
			Q2Assignments.push(this.top30Results[13]);
		}
		if(this.top30Results.length > 16)
		{
			this.top30Results[16].set('newBib',17+bibOffset);
			this.top30Results[16].set('heatStartOrder',4);
			Q2Assignments.push(this.top30Results[16]);
		}
		if(this.top30Results.length > 23)
		{
			this.top30Results[23].set('newBib',24+bibOffset);
			this.top30Results[23].set('heatStartOrder',5);
			Q2Assignments.push(this.top30Results[23]);
		}
		if(this.top30Results.length > 26)
		{
			this.top30Results[26].set('newBib',27+bibOffset);
			this.top30Results[26].set('heatStartOrder',6);
			Q2Assignments.push(this.top30Results[26]);
		}

		//Q3: 5,6,15,16,25,26
		if(this.top30Results.length > 4)
		{
			this.top30Results[4].set('newBib',5+bibOffset);
			this.top30Results[4].set('heatStartOrder',1);
			Q3Assignments.push(this.top30Results[4]);
		}
		if(this.top30Results.length > 5)
		{
			this.top30Results[5].set('newBib',6+bibOffset);
			this.top30Results[5].set('heatStartOrder',2);
			Q3Assignments.push(this.top30Results[5]);
		}
		if(this.top30Results.length > 14)
		{
			this.top30Results[14].set('newBib',15+bibOffset);
			this.top30Results[14].set('heatStartOrder',3);
			Q3Assignments.push(this.top30Results[14]);
		}
		if(this.top30Results.length > 15)
		{
			this.top30Results[15].set('newBib',16+bibOffset);
			this.top30Results[15].set('heatStartOrder',4);
			Q3Assignments.push(this.top30Results[15]);
		}
		if(this.top30Results.length > 24)
		{
			this.top30Results[24].set('newBib',25+bibOffset);
			this.top30Results[24].set('heatStartOrder',5);
			Q3Assignments.push(this.top30Results[24]);
		}
		if(this.top30Results.length > 25)
		{
			this.top30Results[25].set('newBib',26+bibOffset);
			this.top30Results[25].set('heatStartOrder',6);
			Q3Assignments.push(this.top30Results[25]);
		}

		//Q4: 2,9,12,19,22,29
		if(this.top30Results.length > 1)
		{
			this.top30Results[1].set('newBib',2+bibOffset);
			this.top30Results[1].set('heatStartOrder',1);
			Q4Assignments.push(this.top30Results[1]);
		}
		if(this.top30Results.length > 8)
		{
			this.top30Results[8].set('newBib',9+bibOffset);
			this.top30Results[8].set('heatStartOrder',2);
			Q4Assignments.push(this.top30Results[8]);
		}
		if(this.top30Results.length > 11)
		{
			this.top30Results[11].set('newBib',12+bibOffset);
			this.top30Results[11].set('heatStartOrder',3);
			Q4Assignments.push(this.top30Results[11]);
		}
		if(this.top30Results.length > 18)
		{
			this.top30Results[18].set('newBib',19+bibOffset);
			this.top30Results[18].set('heatStartOrder',4);
			Q4Assignments.push(this.top30Results[18]);
		}
		if(this.top30Results.length > 21)
		{
			this.top30Results[21].set('newBib',22+bibOffset);
			this.top30Results[21].set('heatStartOrder',5);
			Q4Assignments.push(this.top30Results[21]);
		}
		if(this.top30Results.length > 28)
		{
			this.top30Results[28].set('newBib',29+bibOffset);
			this.top30Results[28].set('heatStartOrder',6);
			Q4Assignments.push(this.top30Results[28]);
		}
		
		//Q5: 3,8,13,18,23,28
		if(this.top30Results.length > 2)
		{
			this.top30Results[2].set('newBib',3+bibOffset);
			this.top30Results[2].set('heatStartOrder',1);
			Q5Assignments.push(this.top30Results[2]);
		}
		if(this.top30Results.length > 7)
		{
			this.top30Results[7].set('newBib',8+bibOffset);
			this.top30Results[7].set('heatStartOrder',2);
			Q5Assignments.push(this.top30Results[7]);
		}
		if(this.top30Results.length > 12)
		{
			this.top30Results[12].set('newBib',13+bibOffset);
			this.top30Results[12].set('heatStartOrder',3);
			Q5Assignments.push(this.top30Results[12]);
		}
		if(this.top30Results.length > 17)
		{
			this.top30Results[17].set('newBib',18+bibOffset);
			this.top30Results[17].set('heatStartOrder',4);
			Q5Assignments.push(this.top30Results[17]);
		}
		if(this.top30Results.length > 22)
		{
			this.top30Results[22].set('newBib',23+bibOffset);
			this.top30Results[22].set('heatStartOrder',5);
			Q5Assignments.push(this.top30Results[22]);
		}
		if(this.top30Results.length > 27)
		{
			this.top30Results[27].set('newBib',28+bibOffset);
			this.top30Results[27].set('heatStartOrder',6);
			Q5Assignments.push(this.top30Results[27]);
		}
		
		this.Q1Assignments = Q1Assignments;
		this.Q2Assignments = Q2Assignments;
		this.Q3Assignments = Q3Assignments;
		this.Q4Assignments = Q4Assignments;
		this.Q5Assignments = Q5Assignments;
	}
	
	async updateResults()
	{
		//var classGroup = this.sourceClassGroup;

		if(this.sourceClassGroup == null)
		{
			this.top30Results = null;
			this.updateHeatAssignments();
			return;
		}

		var top30Results = [];
		var resultsOffset = parseInt(this.resultsOffset) || 0;
		var hasClassFilter = this.hasClassFilter;
		var selectedClassFilters = this.selectedClassFilters;

		await this.sprintTools.fetchResultsForEvent(this.sourceEvent.id);

		var results = await this.sourceClassGroup.get('resultsSorted');

		var count = 0;
		var skipped = 0;
		for (const result of results)
		{
			// Skip results based on offset FIRST (from full results)
			if(skipped < resultsOffset)
			{
				skipped++;
				continue;
			}

			// Then apply class filter if active
			if (hasClassFilter)
			{
				if (!selectedClassFilters.includes(result.className))
				{
					continue;
				}
			}

			if(count < 30)
			{
				count++;
				result.set('useResultForTie',false);
				top30Results.push(result);
			}
		}

		// Check for ties at the 30th position (only when we have 30 results)
		if (top30Results.length >= 30)
		{
			var lastResult = top30Results[29];
			if (lastResult.isTie)
			{
				// Find if there's a next result that could be tied
				var foundLast = false;
				var pastOffset = 0;
				for (const result of results)
				{
					// Skip past offset first
					if (pastOffset < resultsOffset)
					{
						pastOffset++;
						continue;
					}
					if (hasClassFilter && !selectedClassFilters.includes(result.className))
					{
						continue;
					}
					if (result === lastResult)
					{
						foundLast = true;
						continue;
					}
					if (foundLast && result.isTie)
					{
						var result30time = lastResult.totalTimeFormatted;
						var result31time = result.totalTimeFormatted;
						if (result30time == result31time)
						{
							alert('tie between 30th and 31st place');
							result.set('useResultForTie', false);
							top30Results.push(result);
						}
						break;
					}
					if (foundLast)
					{
						break;
					}
				}
			}
		}

		// Sort by time, break ties by lowest SP points
		top30Results.sort((a, b) => this.sprintTools.sortByTimeWithTieBreaker(a, b));

		this.top30Results = top30Results;
		this.updateHeatAssignments();
	}
}
